import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import TermsCard from './TermsCard';
import ReviewModal from './ReviewModal';
import SalespersonPerformanceCard from './SalespersonPerformanceCard';
import RequestTimeline from './RequestTimeline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const variants = {
    'REQUESTED': 'secondary',
    'APPROVED': 'default',
    'REJECTED': 'destructive',
    'REVOKED': 'outline',
    'CLOSED': 'outline'
  };

  const colors = {
    'REQUESTED': 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    'APPROVED': 'bg-green-500/10 text-green-500 border-green-500/20',
    'REJECTED': 'bg-red-500/10 text-red-500 border-red-500/20',
    'REVOKED': 'bg-gray-500/10 text-gray-500 border-gray-500/20',
    'CLOSED': 'bg-blue-500/10 text-blue-500 border-blue-500/20'
  };

  return (
    <Badge variant={variants[status]} className={colors[status]}>
      {status}
    </Badge>
  );
};

const RequestDetailModal = ({ request, open, onClose, token, userRole, onUpdate, currentUser }) => {
  const [messages, setMessages] = useState([]);
  const [messageBody, setMessageBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closeData, setCloseData] = useState({ outcome: 'WON', revenue: '' });
  const [loggingDemo, setLoggingDemo] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [revieweeName, setRevieweeName] = useState('');
  const [isUserBanned, setIsUserBanned] = useState(false);
  const [trackingLink, setTrackingLink] = useState(null);
  const [loadingTrackingLink, setLoadingTrackingLink] = useState(false);

  const canMessage = request && !['REJECTED', 'REVOKED', 'CLOSED'].includes(request.status);
  const isBusinessOwner = userRole === 'BUSINESS';
  const isSalesperson = userRole === 'SALESPERSON';

  useEffect(() => {
    if (open && request) {
      fetchMessages();
      checkReviewStatus();
      determineRevieweeName();
      checkBanStatus();
      fetchTrackingLink();
    }
  }, [open, request]);

  const checkBanStatus = () => {
    if (!request || !currentUser || userRole !== 'BUSINESS') return;
    
    const bannedUsers = currentUser.banned_users || [];
    setIsUserBanned(bannedUsers.includes(request.salespersonId));
  };

  const fetchTrackingLink = async () => {
    if (!request || request.status !== 'APPROVED') return;
    
    try {
      setLoadingTrackingLink(true);
      const response = await axios.get(
        `${API}/tracking-links/request/${request.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTrackingLink(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch tracking link:', error);
      }
    } finally {
      setLoadingTrackingLink(false);
    }
  };

  const copyTrackingLink = () => {
    if (trackingLink) {
      navigator.clipboard.writeText(trackingLink.short_url);
      toast.success('Tracking link copied to clipboard!');
    }
  };

  const checkReviewStatus = async () => {
    if (request.status !== 'CLOSED') return;
    
    try {
      // Check if current user has already reviewed this request
      const response = await axios.get(`${API}/users/${userRole === 'BUSINESS' ? request.salespersonId : request.post.businessId}/reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check if any review is for this request from current user
      const existingReview = response.data.find(r => r.request_id === request.id);
      setHasReviewed(!!existingReview);
    } catch (error) {
      console.error('Failed to check review status:', error);
    }
  };

  const determineRevieweeName = () => {
    if (!request) return;
    
    if (userRole === 'BUSINESS') {
      // Business reviews salesperson
      setRevieweeName(request.salespersonName || 'Salesperson');
    } else {
      // Salesperson reviews business
      setRevieweeName(request.post?.businessName || 'Business');
    }
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/requests/${request.id}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!messageBody.trim() || messageBody.length > 2000) {
      toast.error('Message must be between 1 and 2000 characters');
      return;
    }

    try {
      setSending(true);
      await axios.post(
        `${API}/requests/${request.id}/messages`,
        { body: messageBody },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessageBody('');
      fetchMessages();
      toast.success('Message sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleApprove = async () => {
    try {
      await axios.patch(
        `${API}/requests/${request.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Request approved!');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    }
  };

  const handleReject = async () => {
    try {
      await axios.patch(
        `${API}/requests/${request.id}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Request rejected');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    }
  };

  const handleUndoReject = async () => {
    try {
      await axios.patch(
        `${API}/requests/${request.id}/undo-reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rejection undone - request is now pending');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo rejection');
    }
  };

  const handleBanUser = async () => {
    if (!window.confirm('Are you sure you want to ban this salesperson from interacting with your business?')) {
      return;
    }
    
    try {
      await axios.post(
        `${API}/business/ban-user/${request.salespersonId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User banned');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban user');
    }
  };

  const handleUnbanUser = async () => {
    try {
      await axios.post(
        `${API}/business/unban-user/${request.salespersonId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User unbanned');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unban user');
    }
  };

  const handleRevoke = async () => {
    try {
      await axios.patch(
        `${API}/requests/${request.id}/revoke`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Request revoked');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke request');
    }
  };

  const handleClose = async () => {
    const revenue = closeData.revenue ? parseFloat(closeData.revenue) : null;
    
    if (closeData.outcome === 'WON' && (!revenue || revenue < 0)) {
      toast.error('Revenue is required and must be >= 0 for WON outcomes');
      return;
    }

    try {
      await axios.patch(
        `${API}/requests/${request.id}/close`,
        { outcome: closeData.outcome, revenue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Request closed successfully!');
      setCloseModalOpen(false);
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close request');
    }
  };

  const handleLogDemo = async () => {
    setLoggingDemo(true);
    try {
      await axios.post(`${API}/business/conversion`, {
        salesperson_id: request.salesperson_id,
        post_id: request.post_id,
        event_type: 'demo'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Demo logged successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log demo');
    } finally {
      setLoggingDemo(false);
    }
  };

  if (!request) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Request Details</span>
              <StatusBadge status={request.status} />
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Request Timeline */}
            <RequestTimeline
              status={request.status}
              termsStatus={request.terms_status}
              outcome={request.outcome}
            />

            {/* Salesperson Performance Card (for business viewing approved+ requests) */}
            {userRole === 'BUSINESS' && ['APPROVED', 'CLOSED'].includes(request.status) && request.salespersonId && (
              <SalespersonPerformanceCard salespersonId={request.salespersonId} token={token} />
            )}

            {/* Request Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{request.post?.title || 'Post'}</CardTitle>
                <CardDescription>{request.post?.category}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Salesperson:</span> {request.salesperson_name} ({request.salesperson_email})
                </div>
                <div>
                  <span className="font-medium">Price:</span> ${request.post?.price}
                </div>
                <div>
                  <span className="font-medium">Requested:</span> {new Date(request.created_at).toLocaleString()}
                </div>
                {request.approved_at && (
                  <div>
                    <span className="font-medium">Approved:</span> {new Date(request.approved_at).toLocaleString()}
                  </div>
                )}
                {request.decided_at && (
                  <div>
                    <span className="font-medium">Decided:</span> {new Date(request.decided_at).toLocaleString()}
                  </div>
                )}
                {request.closed_at && (
                  <div>
                    <span className="font-medium">Closed:</span> {new Date(request.closed_at).toLocaleString()}
                  </div>
                )}
                {request.outcome && (
                  <div>
                    <span className="font-medium">Outcome:</span> {request.outcome}
                  </div>
                )}
                {request.revenue !== null && request.revenue !== undefined && (
                  <div>
                    <span className="font-medium">Revenue:</span> ${request.revenue}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tracking Link Card - Only show for APPROVED requests */}
            {request.status === 'APPROVED' && trackingLink && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    Tracking Link
                  </CardTitle>
                  <CardDescription>Share this link to track clicks and conversions</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-brand-muted">Short URL</Label>
                    <div className="flex gap-2 mt-1">
                      <input
                        type="text"
                        value={trackingLink.short_url}
                        readOnly
                        className="flex-1 h-10 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                      />
                      <Button onClick={copyTrackingLink} variant="outline">
                        Copy
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="space-y-1">
                      <p className="text-xs text-brand-muted">Total Clicks</p>
                      <p className="text-2xl font-bold text-brand-text">{trackingLink.clicks}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-brand-muted">Unique Clicks</p>
                      <p className="text-2xl font-bold text-brand-text">{trackingLink.unique_clicks}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-brand-muted">Demos Generated</p>
                      <p className="text-2xl font-bold text-green-500">{trackingLink.demos_generated}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-brand-muted">Closed Won</p>
                      <p className="text-2xl font-bold text-brand-gold">{trackingLink.closed_won}</p>
                    </div>
                  </div>
                  
                  {trackingLink.revenue_attributed > 0 && (
                    <div className="pt-2 border-t border-zinc-800">
                      <p className="text-xs text-brand-muted">Revenue Attributed</p>
                      <p className="text-2xl font-bold text-brand-gold">${trackingLink.revenue_attributed.toFixed(2)}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Terms Card */}
            <TermsCard 
              request={request} 
              token={token} 
              userRole={userRole} 
              onUpdate={onUpdate}
            />

            {/* Action Buttons */}
            {isBusinessOwner && request.status === 'REQUESTED' && (
              <>
                {request.terms && request.terms.status !== 'ACCEPTED' && (
                  <Alert className="bg-yellow-500/10 border-yellow-500/20">
                    <AlertDescription className="text-yellow-500">
                      ⚠️ Terms must be accepted by both parties before approval
                    </AlertDescription>
                  </Alert>
                )}
                <div className="flex gap-2">
                  <Button 
                    onClick={handleApprove} 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={request.terms && request.terms.status !== 'ACCEPTED'}
                    data-testid="approve-button"
                  >
                    Approve
                  </Button>
                  <Button onClick={handleReject} variant="destructive" className="flex-1">
                    Reject
                  </Button>
                </div>
              </>
            )}

            {/* Undo Rejection Button */}
            {isBusinessOwner && request.status === 'REJECTED' && (
              <div className="flex gap-2">
                <Button 
                  onClick={handleUndoReject} 
                  className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                >
                  Undo Rejection
                </Button>
              </div>
            )}

            {/* Ban/Unban Button */}
            {isBusinessOwner && request.salespersonId && (
              <div className="border-t border-zinc-800 pt-4">
                {isUserBanned ? (
                  <Button 
                    onClick={handleUnbanUser} 
                    variant="outline"
                    className="w-full border-green-500 text-green-500 hover:bg-green-500/10"
                  >
                    Unban This Salesperson
                  </Button>
                ) : (
                  <Button 
                    onClick={handleBanUser} 
                    variant="outline"
                    className="w-full border-red-500 text-red-500 hover:bg-red-500/10"
                  >
                    Ban This Salesperson
                  </Button>
                )}
              </div>
            )}

            {isBusinessOwner && request.status === 'APPROVED' && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button onClick={handleRevoke} variant="outline" className="flex-1">
                    Revoke Assignment
                  </Button>
                  <Button onClick={() => setCloseModalOpen(true)} className="flex-1 bg-brand-gold hover:bg-brand-gold/90">
                    Close Request
                  </Button>
                </div>
                <Button
                  onClick={handleLogDemo}
                  disabled={loggingDemo}
                  variant="outline"
                  className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                  data-testid="log-demo-btn"
                >
                  {loggingDemo ? 'Logging...' : 'Log Demo'}
                </Button>
              </div>
            )}

            {isSalesperson && request.status === 'REQUESTED' && (
              <Button onClick={handleRevoke} variant="outline" className="w-full">
                Revoke Request
              </Button>
            )}

            {/* Review Button */}
            {request.status === 'CLOSED' && (
              <Card className="bg-brand-gold/5 border-brand-gold/20">
                <CardContent className="pt-6">
                  {hasReviewed ? (
                    <Alert className="bg-green-500/10 border-green-500/20">
                      <AlertDescription className="text-green-500">
                        ✓ You have already reviewed {revieweeName}
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <div className="text-center space-y-3">
                      <p className="text-sm text-brand-muted">
                        How was your experience working with {revieweeName}?
                      </p>
                      <Button
                        onClick={() => setReviewModalOpen(true)}
                        className="bg-brand-gold hover:bg-brand-gold/90 text-black"
                      >
                        Leave a Review
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Messaging */}
            <Card>
              <CardHeader>
                <CardTitle className="text-md">Messages</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-4">Loading messages...</div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-4 text-brand-muted">No messages yet</div>
                ) : (
                  <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
                    {messages.map((msg) => (
                      <div key={msg.id} className="p-3 bg-brand-surface rounded-lg">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-sm">{msg.sender_name}</span>
                          <span className="text-xs text-brand-muted">{new Date(msg.ts).toLocaleString()}</span>
                        </div>
                        <p className="text-sm text-brand-text">{msg.body}</p>
                      </div>
                    ))}
                  </div>
                )}

                {canMessage && (
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Type your message..."
                      value={messageBody}
                      onChange={(e) => setMessageBody(e.target.value)}
                      maxLength={2000}
                      rows={3}
                    />
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-brand-muted">{messageBody.length}/2000</span>
                      <Button onClick={sendMessage} disabled={sending || !messageBody.trim()}>
                        {sending ? 'Sending...' : 'Send'}
                      </Button>
                    </div>
                  </div>
                )}

                {!canMessage && (
                  <div className="text-center py-4 text-brand-muted">
                    Messaging is disabled for {request.status} requests
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Close Request Modal */}
      <Dialog open={closeModalOpen} onOpenChange={setCloseModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Outcome</Label>
              <Select value={closeData.outcome} onValueChange={(val) => setCloseData({ ...closeData, outcome: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="WON">WON</SelectItem>
                  <SelectItem value="LOST">LOST</SelectItem>
                  <SelectItem value="ABANDONED">ABANDONED</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {closeData.outcome === 'WON' && (
              <div>
                <Label>Revenue ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={closeData.revenue}
                  onChange={(e) => setCloseData({ ...closeData, revenue: e.target.value })}
                />
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCloseModalOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleClose} className="flex-1 bg-brand-gold hover:bg-brand-gold/90">
                Close Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <ReviewModal
        isOpen={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        requestId={request.id}
        revieweeName={revieweeName}
        token={token}
        onSuccess={() => {
          setHasReviewed(true);
          onUpdate();
        }}
      />
    </>
  );
};

export { StatusBadge, RequestDetailModal };
